import { SAVE_SCHEMA_VERSION } from "../../config/gameplay";
import { finiteOr, nonNegative, nonNegativeInteger } from "../../utils/math";
import { crateCorePatterns, crateShellSkins } from "../crates/catalog";
import { upgradeDefinitions, upgradeKinds, type UpgradeLevels } from "../upgrades/definitions";
import { createDefaultCosmetics, createDefaultSettings, createEmptyStats, createFreshSave, periodForUnixSeconds } from "./defaults";
import { sanitizeAutoLaunch } from "./sanitize-auto-launch";
import { sanitizeCrates } from "./sanitize-crates";
import { sanitizeTimedEvents } from "./sanitize-events";
import type {
  CosmeticState,
  OrbitForgeSave,
  PeriodState,
  PlayerSettings,
  ProgressStats
} from "./types";

const statKeys = Object.keys(createEmptyStats()) as (keyof ProgressStats)[];

export function sanitizeSave(raw: unknown, nowUnixSeconds: number): OrbitForgeSave {
  const fresh = createFreshSave(nowUnixSeconds);
  if (!isRecord(raw)) {
    return fresh;
  }

  const stats = sanitizeStats(raw.stats);
  const periods = sanitizePeriods(raw.periods, nowUnixSeconds);
  resetPeriodStats(stats, periods, nowUnixSeconds);

  return {
    schemaVersion: SAVE_SCHEMA_VERSION,
    createdAtUnixSeconds: nonNegative(raw.createdAtUnixSeconds, nowUnixSeconds),
    updatedAtUnixSeconds: nowUnixSeconds,
    lastSeenUnixSeconds: nonNegative(raw.lastSeenUnixSeconds, nowUnixSeconds),
    rngState: sanitizeRng(raw.rngState),
    nextEntityId: Math.max(1, nonNegativeInteger(raw.nextEntityId, 1)),
    previousProgressScore: nonNegative(raw.previousProgressScore),
    tutorialDismissed: raw.tutorialDismissed === true,
    stats,
    upgrades: sanitizeUpgrades(raw.upgrades),
    cosmetics: sanitizeCosmetics(raw.cosmetics),
    crates: sanitizeCrates(raw.crates),
    autoLaunch: sanitizeAutoLaunch(raw.autoLaunch),
    events: sanitizeTimedEvents(raw.events),
    settings: sanitizeSettings(raw.settings),
    periods: periodForUnixSeconds(nowUnixSeconds)
  };
}

export function hasMeaningfulProgress(save: OrbitForgeSave): boolean {
  const stats = save.stats;
  return (
    stats.Credits > 0.5 ||
    stats.TotalEarnedCredits > 0.5 ||
    stats.TotalPlayTimeSeconds > 10 ||
    stats.TotalCoresLaunched > 0 ||
    stats.TotalCoresCollected > 0 ||
    stats.TotalChargeBounces > 0 ||
    stats.ReactorStormEventsCompleted > 0 ||
    stats.OverchargedCoresLaunched > 0 ||
    stats.SupplyJackpotsWon > 0 ||
    stats.DailyIgnitionClaims > 0 ||
    stats.AutoLaunchDurationLevel > 0 ||
    stats.AutoLaunchRateLevel > 0 ||
    save.cosmetics.UnlockedAchievements.length > 0 ||
    save.cosmetics.UnlockedCrateCorePatterns.length > 0 ||
    save.cosmetics.UnlockedCrateShellSkins.length > 0 ||
    upgradeKinds.some((kind) => save.upgrades[kind] > 0)
  );
}

export function progressScore(save: OrbitForgeSave): number {
  const stats = save.stats;
  const upgradeScore = upgradeKinds.reduce((sum, kind) => sum + save.upgrades[kind], 0) * 500;
  return (
    upgradeScore +
    stats.TotalEarnedCredits * 0.02 +
    stats.TotalPlayTimeSeconds * 2 +
    stats.TotalCoresLaunched * 8 +
    stats.TotalCoresCollected * 16 +
    stats.TotalChargeBounces * 1.5 +
    stats.ReactorStormEventsCompleted * 120 +
    stats.OverchargedCoresLaunched * 80 +
    stats.SupplyJackpotsWon * 180 +
    stats.FluxHockeyWins * 160 +
    stats.FluxHockeyMatches * 42 +
    stats.AutoLaunchDurationLevel * 240 +
    stats.AutoLaunchRateLevel * 280 +
    Math.max(1, stats.BestMultiplier) * 40 +
    stats.BestPolarityStack * 35 +
    save.cosmetics.UnlockedAchievements.length * 350 +
    save.cosmetics.UnlockedCrateShellSkins.length * 260 +
    save.cosmetics.UnlockedCrateCorePatterns.length * 260
  );
}

export function chooseBestSave(primary: OrbitForgeSave, backup: OrbitForgeSave): OrbitForgeSave {
  const primaryMeaningful = hasMeaningfulProgress(primary);
  const backupMeaningful = hasMeaningfulProgress(backup);

  if (!primaryMeaningful && backupMeaningful) {
    return backup;
  }

  if (primaryMeaningful && !backupMeaningful) {
    return primary;
  }

  return progressScore(backup) > progressScore(primary) ? backup : primary;
}

function sanitizeStats(raw: unknown): ProgressStats {
  const empty = createEmptyStats();
  if (!isRecord(raw)) {
    return empty;
  }

  const stats = { ...empty };
  for (const key of statKeys) {
    const value = key === "BestMultiplier" ? Math.max(1, nonNegative(raw[key], empty[key])) : nonNegative(raw[key], empty[key]);
    stats[key] = value;
  }
  stats.AutoLaunchTickets60 += nonNegativeInteger(raw.AutoLaunchTickets30);
  stats.DailyIgnitionActive = Math.min(1, nonNegativeInteger(raw.DailyIgnitionActive));
  trimAutoLaunchTicketStorage(stats);
  return stats;
}

function trimAutoLaunchTicketStorage(stats: ProgressStats): void {
  stats.AutoLaunchTickets60 = nonNegativeInteger(stats.AutoLaunchTickets60);
  stats.AutoLaunchTickets120 = nonNegativeInteger(stats.AutoLaunchTickets120);
  stats.AutoLaunchTickets300 = nonNegativeInteger(stats.AutoLaunchTickets300);
  stats.AutoLaunchTickets600 = nonNegativeInteger(stats.AutoLaunchTickets600);

  let overflow =
    stats.AutoLaunchTickets60 +
    stats.AutoLaunchTickets120 +
    stats.AutoLaunchTickets300 +
    stats.AutoLaunchTickets600 -
    5;
  for (const key of ["AutoLaunchTickets60", "AutoLaunchTickets120", "AutoLaunchTickets300", "AutoLaunchTickets600"] as const) {
    if (overflow <= 0) {
      break;
    }
    const removed = Math.min(stats[key], overflow);
    stats[key] -= removed;
    overflow -= removed;
  }
}

function sanitizeUpgrades(raw: unknown): UpgradeLevels {
  const levels = Object.fromEntries(upgradeKinds.map((kind) => [kind, 0])) as UpgradeLevels;
  if (!isRecord(raw)) {
    return levels;
  }

  for (const definition of upgradeDefinitions) {
    levels[definition.kind] = Math.min(definition.maxLevel, nonNegativeInteger(raw[definition.kind]));
  }
  return levels;
}

function sanitizeCosmetics(raw: unknown): CosmeticState {
  const defaults = createDefaultCosmetics();
  if (!isRecord(raw)) {
    return defaults;
  }

  const unlockedShellSkins = knownStringArray(raw.UnlockedCrateShellSkins, crateShellSkins.map((skin) => skin.id));
  const unlockedCorePatterns = knownStringArray(raw.UnlockedCrateCorePatterns, crateCorePatterns.map((pattern) => pattern.id));
  const selectedShellSkin = selectedCosmeticOrFallback(
    raw.SelectedShellSkin,
    defaults.SelectedShellSkin,
    ["white_arc", "cyan_pulse", "lime_flux", "pink_circuit", "gold_relay", "violet_nova"],
    unlockedShellSkins
  );
  const selectedCorePattern = selectedCosmeticOrFallback(
    raw.SelectedCorePattern,
    defaults.SelectedCorePattern,
    ["clean_core", "hollow_core", "target_core", "split_core", "spark_core", "twin_core", "crown_core"],
    unlockedCorePatterns
  );

  return {
    SelectedShellSkin: selectedShellSkin,
    SelectedCorePattern: selectedCorePattern,
    UnlockedCrateShellSkins: unlockedShellSkins,
    UnlockedCrateCorePatterns: unlockedCorePatterns,
    PrestigeSquareModeEnabled: raw.PrestigeSquareModeEnabled === true,
    PrestigeArenaPreferenceSet: raw.PrestigeArenaPreferenceSet === true,
    UnlockedAchievements: stringArray(raw.UnlockedAchievements)
  };
}

function sanitizeSettings(raw: unknown): PlayerSettings {
  const defaults = createDefaultSettings();
  if (!isRecord(raw)) {
    return defaults;
  }

  return defaults;
}

function sanitizePeriods(raw: unknown, nowUnixSeconds: number): PeriodState {
  const current = periodForUnixSeconds(nowUnixSeconds);
  if (!isRecord(raw)) {
    return current;
  }

  return {
    dayId: nonNegativeInteger(raw.dayId, current.dayId),
    weekId: nonNegativeInteger(raw.weekId, current.weekId)
  };
}

function resetPeriodStats(stats: ProgressStats, savedPeriod: PeriodState, nowUnixSeconds: number): void {
  const current = periodForUnixSeconds(nowUnixSeconds);
  if (savedPeriod.dayId !== current.dayId) {
    stats.DailyEarnedCredits = 0;
    stats.DailyPlayTimeSeconds = 0;
    stats.DailyCoresLaunched = 0;
    stats.DailyCoresCollected = 0;
    stats.DailyChargeBounces = 0;
  }

  if (savedPeriod.weekId !== current.weekId) {
    stats.WeeklyEarnedCredits = 0;
    stats.WeeklyPlayTimeSeconds = 0;
    stats.WeeklyCoresLaunched = 0;
    stats.WeeklyCoresCollected = 0;
    stats.WeeklyChargeBounces = 0;
  }
}

function sanitizeRng(raw: unknown): number {
  return Math.max(1, Math.floor(finiteOr(raw, 0x8f17_2026))) >>> 0;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function stringOr(value: unknown, fallback: string): string {
  return typeof value === "string" && value.length > 0 ? value : fallback;
}

function stringArray(value: unknown): readonly string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string" && item.length > 0) : [];
}

function knownStringArray(value: unknown, knownIds: readonly string[]): readonly string[] {
  return Array.from(new Set(stringArray(value).filter((item) => knownIds.includes(item))));
}

function selectedCosmeticOrFallback(value: unknown, fallback: string, levelIds: readonly string[], crateIds: readonly string[]): string {
  const selected = stringOr(value, fallback);
  return levelIds.includes(selected) || crateIds.includes(selected) ? selected : fallback;
}
